import { Link } from 'react-router-dom';
import { Heart, Play } from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import { useState, memo, useRef, useEffect } from 'react';
import { useWishlist } from '../context/WishlistContext';
import { useBrands } from '../context/BrandContext';
import { useCurrency } from '../context/CurrencyContext';
import { useConfetti } from '../hooks/useConfetti';
import { optimizeImage, generateSrcSet, getImageSizes } from '../lib/imageUtils';

// Helper function to convert color names to hex codes
const getColorHex = (colorName) => {
  const colorMap = {
    // Basic colors
    'black': '#000000',
    'white': '#FFFFFF',
    'red': '#FF0000',
    'blue': '#0000FF',
    'green': '#00FF00',
    'yellow': '#FFFF00',
    'orange': '#FF8C00',
    'purple': '#800080',
    'pink': '#FFC0CB',
    'brown': '#8B4513',
    'gray': '#808080',
    'grey': '#808080',

    // Extended colors
    'light blue': '#87CEEB',
    'light-blue': '#87CEEB',
    'lightblue': '#87CEEB',
    'dark blue': '#00008B',
    'navy': '#000080',
    'sky blue': '#87CEEB',
    'baby blue': '#89CFF0',

    'light green': '#90EE90',
    'dark green': '#006400',
    'lime': '#00FF00',
    'olive': '#808000',

    'light pink': '#FFB6C1',
    'hot pink': '#FF69B4',
    'magenta': '#FF00FF',

    'light yellow': '#FFFFE0',
    'gold': '#FFD700',

    'light orange': '#FFA500',
    'dark orange': '#FF8C00',
    'coral': '#FF7F50',

    'light purple': '#DDA0DD',
    'violet': '#EE82EE',
    'lavender': '#E6E6FA',

    'beige': '#F5F5DC',
    'tan': '#D2B48C',
    'cream': '#FFFDD0',

    'light gray': '#D3D3D3',
    'light grey': '#D3D3D3',
    'dark gray': '#A9A9A9',
    'dark grey': '#A9A9A9',
    'silver': '#C0C0C0',

    'maroon': '#800000',
    'burgundy': '#800020',
    'wine': '#722F37',

    'teal': '#008080',
    'cyan': '#00FFFF',
    'turquoise': '#40E0D0',

    'mint': '#98FF98',
    'sage': '#9DC183',

    'peach': '#FFE5B4',
    'apricot': '#FBCEB1',

    'khaki': '#C3B091',
    'sand': '#C2B280',

    'indigo': '#4B0082',
    'plum': '#DDA0DD',
  };

  const normalized = colorName.toLowerCase().trim();
  return colorMap[normalized] || '#CCCCCC'; // Default to gray if color not found
};

export const ProductCard = memo(({ product, index = 0 }) => {
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { getBrand, getBrandColor } = useBrands();
  const { format } = useCurrency();
  const { triggerHeartConfetti } = useConfetti();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const videoRef = useRef(null);
  const shouldReduceMotion = useReducedMotion();
  const inWishlist = isInWishlist(product.id);

  const brand = getBrand(product.brand);
  const brandColor = getBrandColor(product.brand);

  // Check if product has a video (check for common video file extensions)
  const videoUrl = product.video || product.images?.find(img =>
    typeof img === 'string' && (
      img.endsWith('.mp4') ||
      img.endsWith('.webm') ||
      img.endsWith('.mov') ||
      img.includes('.mp4?') ||
      img.includes('.webm?') ||
      img.includes('.mov?')
    )
  );

  // Get the first non-video image
  const firstImage = product.images?.find(img =>
    typeof img === 'string' && !(
      img.endsWith('.mp4') ||
      img.endsWith('.webm') ||
      img.endsWith('.mov') ||
      img.includes('.mp4?') ||
      img.includes('.webm?') ||
      img.includes('.mov?')
    )
  ) || product.images?.[0];

  // Handle video playback on hover
  useEffect(() => {
    if (!videoRef.current || !videoUrl) return;

    const video = videoRef.current;

    if (isHovering) {
      const playVideo = () => {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.error('Video play error:', error);
          });
        }
      };

      if (video.readyState >= 2) {
        playVideo();
      } else {
        // preload="none" means we must trigger load() on first hover before play()
        const handleLoadedData = () => playVideo();
        video.addEventListener('loadeddata', handleLoadedData, { once: true });
        if (video.readyState === 0) video.load();

        return () => {
          video.removeEventListener('loadeddata', handleLoadedData);
        };
      }
    } else {
      video.pause();
      video.currentTime = 0;
    }
  }, [isHovering, videoUrl]);

  return (
    <motion.div
      initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      whileHover={shouldReduceMotion ? {} : {
        scale: 1.05,
        rotate: 1,
        y: -8,
        transition: { type: 'spring', stiffness: 300 }
      }}
      className="group"
      data-testid={`product-card-${product.id}`}
    >
      <div className="card-product relative overflow-hidden transition-shadow duration-300">
        {/* Wishlist Button */}
        <motion.button
          whileHover={{ scale: 1.1, rotate: inWishlist ? 0 : [0, -10, 10, -10, 0] }}
          whileTap={{ scale: 0.9 }}
          data-testid={`wishlist-btn-${product.id}`}
          data-cursor="LOVE"
          onClick={(e) => {
            e.preventDefault();
            const wasInWishlist = isInWishlist(product.id);
            toggleWishlist(product);
            if (!wasInWishlist) {
              triggerHeartConfetti();
            }
          }}
          aria-label={inWishlist ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
          className={`absolute top-4 right-4 z-20 p-2 border-2 border-black transition-all ${inWishlist
            ? 'text-black'
            : 'bg-white hover:text-black'
            }`}
          style={{ backgroundColor: inWishlist ? brandColor : undefined }}
          onMouseEnter={(e) => {
            if (!inWishlist) e.currentTarget.style.backgroundColor = brandColor;
          }}
          onMouseLeave={(e) => {
            if (!inWishlist) e.currentTarget.style.backgroundColor = 'white';
          }}
        >
          <motion.div
            animate={inWishlist ? { scale: [1, 1.3, 1] } : {}}
            transition={{ duration: 0.3 }}
          >
            <Heart size={18} fill={inWishlist ? 'currentColor' : 'none'} aria-hidden="true" />
          </motion.div>
        </motion.button>

        {/* Brand Badge */}
        <motion.span
          whileHover={{ scale: 1.1, rotate: -2 }}
          className="inline-block px-3 py-1 text-xs font-bold uppercase tracking-wider border-2 border-black absolute top-4 left-4 z-20"
          style={{ backgroundColor: brandColor }}
        >
          {product.brand}
        </motion.span>

        {/* Sale Badge */}
        {product.sale_price && product.orig_price && product.sale_price < product.orig_price && (
          <motion.span
            animate={shouldReduceMotion ? {} : {
              rotate: [-5, 5, -5],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="absolute top-14 left-4 z-20 bg-[#FFF44F] border-2 border-black px-2 py-0.5 text-[10px] font-black uppercase tracking-tighter shadow-[2px_2px_0px_0px_#000000]"
          >
            SALE
          </motion.span>
        )}

        {/* Image/Video */}
        <Link
          to={`/product/${product.slug || product.id}`}
          data-testid={`product-link-${product.id}`}
          data-cursor="VIEW"
        >
          <div
            className="aspect-square overflow-hidden mb-4 relative bg-[#FDFBF7]"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <motion.img
              initial={index < 2 ? { opacity: 1 } : { opacity: 0 }}
              animate={{ opacity: imageLoaded ? 1 : (index < 2 ? 1 : 0) }}
              transition={{ duration: 0.5 }}
              onLoad={() => setImageLoaded(true)}
              src={optimizeImage(firstImage || product.images?.[0] || 'https://placehold.co/400', { width: 400, height: 400, quality: 80 })}
              srcSet={generateSrcSet(firstImage || product.images?.[0], [400, 600, 800])}
              sizes={getImageSizes('card')}
              alt={product.name}
              className={`w-full h-full object-contain transition-all duration-500 ${
                videoUrl && isHovering ? 'opacity-0' : 'group-hover:scale-110'
              }`}
              loading={index < 2 ? "eager" : "lazy"}
              fetchPriority={index < 2 ? "high" : "auto"}
              width="400"
              height="400"
            />
            {videoUrl && (
              <div className="absolute bottom-4 right-4 z-20 bg-white/90 border-2 border-black p-1 transition-opacity duration-300 group-hover:opacity-0">
                <Play size={14} fill="black" />
              </div>
            )}
            {videoUrl && (
              <video
                ref={videoRef}
                muted
                loop
                playsInline
                preload="none"
                className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 z-10 ${
                  isHovering ? 'opacity-100' : 'opacity-0'
                }`}
                poster={optimizeImage(firstImage || 'https://placehold.co/400', { width: 400, height: 400, quality: 80 })}
              >
                <source src={videoUrl} type={videoUrl.includes('.webm') ? 'video/webm' : videoUrl.includes('.mov') ? 'video/quicktime' : 'video/mp4'} />
              </video>
            )}
            {!imageLoaded && (
              <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">pop.</span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="space-y-2">
            <div className="min-h-[4rem]">
              <h3 className="font-body font-bold text-base line-clamp-1 group-hover:underline">
                {product.name}
              </h3>
              {product.material && (
                <div className="flex items-center gap-1 mt-1">
                  <motion.span
                    whileHover={{ scale: 1.05, backgroundColor: '#000', color: '#fff' }}
                    className="text-[10px] px-2 py-0.5 bg-white border border-black text-black font-semibold uppercase tracking-wider transition-colors duration-200"
                  >
                    {product.material}
                  </motion.span>
                </div>
              )}
            </div>
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2">
                <span className="price-tag">
                  {format(product.sale_price ?? product.price)}
                </span>
                {product.orig_price && product.sale_price && product.sale_price < product.orig_price && (
                  <span className="text-xs text-gray-500 line-through">
                    {format(product.orig_price)}
                  </span>
                )}
              </div>
              <div className="flex gap-1">
                {(product.sizes || []).slice(0, 4).map((size) => (
                  <span key={size} className="text-xs text-gray-500">
                    {size}
                  </span>
                ))}
                {(product.sizes || []).length > 4 && (
                  <span className="text-xs text-gray-500">+{(product.sizes || []).length - 4}</span>
                )}
              </div>
            </div>

            {/* Color circles - show available colorways */}
            {product.colors && product.colors.length > 1 && (
              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-200">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold mr-1">
                  Colors:
                </span>
                {product.colors.map((color) => (
                  <motion.div
                    key={color}
                    whileHover={shouldReduceMotion ? {} : { scale: 1.2, rotate: 8 }}
                    whileTap={shouldReduceMotion ? {} : { scale: 0.9 }}
                    transition={{ type: "spring", stiffness: 400, damping: 12 }}
                    className="w-5 h-5 rounded-full border-2 border-black"
                    style={{ backgroundColor: getColorHex(color) }}
                    title={color}
                    role="img"
                    aria-label={`Available in ${color}`}
                  />
                ))}
              </div>
            )}
          </div>
        </Link>
      </div>
    </motion.div>
  );
});
